import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "motion/react";
import { NavbarV2 } from "@/components/landing-v2/NavbarV2";
import { Footer } from "@/components/landing/Footer";
import { Badge } from "@/components/ui/badge";
import { getPostBySlug } from "@/lib/blog-posts";
import type { ContentBlock } from "@/lib/blog-posts";
import { cn } from "@/lib/utils";

function ProseBlock({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case "p":
      return (
        <p className="text-foreground/80 text-base leading-[1.75] sm:text-[17px]">
          {block.text}
        </p>
      );
    case "h2":
      return (
        <h2 className="font-display text-foreground text-2xl font-normal tracking-tight sm:text-3xl">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="text-foreground text-lg font-semibold sm:text-xl">
          {block.text}
        </h3>
      );
    case "ul":
      return (
        <ul className="space-y-2 pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="text-foreground/80 flex gap-3 text-base leading-[1.7] sm:text-[17px]">
              <span className="text-brand mt-[0.4em] shrink-0 text-xs">●</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "code":
      return (
        <div className="overflow-x-auto rounded-xl border border-border bg-muted/40 dark:bg-white/[0.04]">
          <pre className="p-5 text-[13px] leading-relaxed">
            <code className="text-foreground/85 font-mono">{block.code}</code>
          </pre>
        </div>
      );
    case "callout":
      return (
        <blockquote className="border-brand/40 text-foreground/75 border-l-2 pl-5 text-base italic leading-[1.75] sm:text-[17px]">
          {block.text}
        </blockquote>
      );
  }
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="bg-background min-h-screen">
      <NavbarV2 />

      <main>
        {/* Hero */}
        <section
          className={cn(
            "relative overflow-hidden pt-32 pb-12 sm:pt-40 sm:pb-16",
          )}
        >
          {/* Gradient wash behind the header */}
          <div
            className={cn(
              "pointer-events-none absolute inset-0 bg-gradient-to-b",
              post.gradient,
              "opacity-30",
            )}
          />

          <div className="relative mx-auto max-w-2xl px-6">
            {/* Back link */}
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/blog"
                className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1.5 text-sm transition-colors"
              >
                <span aria-hidden>←</span>
                <span>All posts</span>
              </Link>
            </motion.div>

            {/* Meta */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mt-4 flex items-center gap-3"
            >
              <Badge variant="secondary">{post.category}</Badge>
              <span className="text-muted-foreground text-sm">{post.date}</span>
              <span className="text-muted-foreground/50 text-sm">·</span>
              <span className="text-muted-foreground text-sm">{post.readTime}</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-foreground mt-5 text-3xl font-normal leading-[1.15] tracking-tight sm:text-4xl md:text-5xl"
            >
              {post.title}
            </motion.h1>

            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="text-muted-foreground mt-5 text-base leading-relaxed sm:text-lg"
            >
              {post.excerpt}
            </motion.p>

            {/* Divider */}
            <motion.hr
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="border-border mt-10"
            />
          </div>
        </section>

        {/* Article body */}
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto max-w-2xl px-6 pb-24 pt-10"
        >
          <div className="space-y-6">
            {post.content.map((block, i) => (
              <ProseBlock key={i} block={block} />
            ))}
          </div>

          {/* Footer nav */}
          <div className="border-border mt-16 border-t pt-10">
            <Link
              to="/blog"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
            >
              <span aria-hidden>←</span>
              <span>Back to all posts</span>
            </Link>
          </div>
        </motion.article>
      </main>

      <Footer />
    </div>
  );
}
